<template>
  <v-stepper-vertical-item
    :value="stepValue"
    :elevation="0"
    :class="{ 'step-completed': currentStep > stepValue }"
  >
    <template #title>
      <div class="subtitle1">{{ $t("modals.allowCloudService.stepJourneyType.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > stepValue" icon="$check" />
      <span v-else>{{ stepNumber }}</span>
    </template>
    <div>
      <p class="body2 mb-6">{{ $t("modals.allowCloudService.stepJourneyType.description") }}</p>

      <button-toggle-group
        :model-value="modelValue.journeyType ?? SatJourneyType.STANDARD"
        :items="journeyTypeItems"
        class="mb-6 flex-grow-1 w-100 journey-type-toggle"
        density="comfortable"
        bg-color="indigo-faint"
        @update:modelValue="onJourneyTypeChange"
      />

      <div class="mt-5 d-flex">
        <v-btn color="primary" rounded min-width="120" :loading="loading" @click="moveToNextStep">
          {{ $t("modals.allowCloudService.stepJourneyType.actionBtn") }}
        </v-btn>
        <v-btn class="ml-2" variant="text" color="primary" @click="goBack">
          {{ $t("general.back") }}
        </v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import {
  SatConfigurationStatus,
  SatJourneyType,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { Service } from "@/constants/cloud-apps.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import type { SatSettings } from "@/_store/security-awareness/security-awareness.adapters.ts";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";
import ButtonToggleGroup, {
  type ButtonToggleItem,
  type ButtonToggleValue,
} from "@/components/ButtonToggleGroup.vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  components: {
    ButtonToggleGroup,
  },
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    stepValue: {
      type: Number,
      required: true,
    },
    stepNumber: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
  },
  emits: ["update:modelValue", "next", "back"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const securityAwarenessStore = useSecurityAwarenessModule();
    const loading = ref(false);

    const formValue = computed(() => props.modelValue);

    const updateLocalValue = (partial: Partial<SatConnectionConfigForm>) => {
      emit("update:modelValue", { ...props.modelValue, ...partial });
    };

    const onJourneyTypeChange = (value: ButtonToggleValue) => {
      if (value !== SatJourneyType.STANDARD && value !== SatJourneyType.RANDOM) {
        return;
      }

      updateLocalValue({ journeyType: value });
    };

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const journeyTypeItems = computed<ButtonToggleItem[]>(() => [
      {
        value: SatJourneyType.STANDARD,
        label: i18n.t("modals.allowCloudService.stepJourneyType.standard"),
      },
      {
        value: SatJourneyType.RANDOM,
        label: i18n.t("modals.allowCloudService.stepJourneyType.random"),
      },
    ]);

    const moveToNextStep = async () => {
      loading.value = true;
      await securityAwarenessStore.updateSettings({
        randomJourney: formValue.value.journeyType === SatJourneyType.RANDOM,
        emailProviderConfigurations: {
          [satConfigurationProvider.value]: {
            status: SatConfigurationStatus.JOURNEY_TYPE_SELECTED,
          },
        },
      } as Partial<SatSettings>);
      loading.value = false;
      emit("next");
    };

    const goBack = () => {
      emit("back");
    };

    return {
      SatJourneyType,
      journeyTypeItems,
      loading,
      onJourneyTypeChange,
      moveToNextStep,
      goBack,
    };
  },
});
</script>

<style scoped lang="scss">
.journey-type-toggle {
  max-width: 320px;
}
</style>
